import React, { useEffect, useState } from 'react';
import { EntityUIState, EntityUIStateWrapper } from '../../../core/interfaces/entitiy-ui-state';
import { AccountAccessService } from '../../../core/services/account-access.service';
import { getErrorMessageFromObj } from '../../../core/utils/request';
import { PayloadViewerData, PayloadViewerOrder } from '../../interfaces/payloadViewerData';
import useService from '../../../injector/hooks/useService';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import JsonEditor from '../../../code/components/json-editor/JsonEditor';
import RetryApiAlert from '../../../fallback-screens/components/retry-api-alert/RetryApiAlert';
import { HdIconButton } from '../../../components';
import HdButton from '../../../button/components/button/HdButton';
import HdIcon from '../../../media/components/icon/HdIcon';
import { StickyProgressBar } from '../../../progress-indicators/components/sticky-progress-bar/StickyProgressBar';
import { UserConsentError } from '../../../access-control/components/user-consent-error/UserConsentError';
import { PayloadViewerDialogShimmer } from './PayloadViewerDialogShimmer';
import styles from './PayloadViewerDialog.module.scss';

export interface PayloadViewerDialogProps {
  editorRef: any;
  title: string;
  subtitle?: string;
  onNegativeClick?: any;
  renderNext?: boolean;
  renderPrev?: boolean;
  getPayloadPromise: (order: string) => Promise<PayloadViewerData>;
}

const payloadViewerDialogDataIdGenerator = getDataIdGenerator('payloadViewerDialog');

export function PayloadViewerDialog({
  editorRef,
  title,
  subtitle,
  onNegativeClick,
  renderNext,
  renderPrev,
  getPayloadPromise
}: PayloadViewerDialogProps) {
  const [uiState, setUIState] = useState<EntityUIStateWrapper & { emptyMsg?: string }>({
    state: EntityUIState.LOADING
  });
  const [hasPrev, setHasPrev] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [payload, setPayload] = useState(null);

  const accountAccessService = useService(AccountAccessService);
  const showUserAccessError = !accountAccessService.getAccountAccessConsentStatus();

  useEffect(() => {
    getPayload(undefined);
  }, []);

  useEffect(() => {
    const documentKeyboardListener: any = document.addEventListener('keyup', e => {
      if (renderNext && e.keyCode === 39) {
        getPayload('next');
      }

      if (renderPrev && e.keyCode === 37) {
        getPayload('prev');
      }
    });

    return () => {
      document.removeEventListener('keyup', documentKeyboardListener);
    };
  }, []);

  const getPayload = (order: string) => {
    setUIState({
      state: EntityUIState.LOADING
    });

    getPayloadPromise(order).then(
      (data: PayloadViewerData) => {
        setPayload(getFormattedPayload(data.payload));

        setHasNext(data.hasNext);
        setHasPrev(data.hasPrev);

        if (data.payload) {
          setUIState({
            state: EntityUIState.IDLE
          });
        } else {
          setUIState({
            state: EntityUIState.EMPTY,
            emptyMsg: 'Empty payload found'
          });
        }
      },
      (error: any) => {
        if (error.status === 404) {
          setUIState({
            state: EntityUIState.EMPTY,
            emptyMsg: getErrorMessageFromObj(error)
          });

          return;
        }

        setUIState({
          state: EntityUIState.ERRORED,
          error
        });
      }
    );
  };

  const getFormattedPayload = (rawPayload: any) => {
    if (rawPayload === null) {
      return undefined;
    }

    if (typeof rawPayload === 'object') {
      return JSON.stringify(rawPayload, null, '\t');
    }
    try {
      const formattedPayload = JSON.parse(rawPayload);
      return JSON.stringify(formattedPayload, undefined, '\t');
    } catch (exception) {
      return rawPayload;
    }
  };

  return (
    <>
      <div
        className='dialog-header dialog-header--absolute'
        tabIndex={0}
        data-id={payloadViewerDialogDataIdGenerator('')}
      >
        <HdIconButton
          onClick={onNegativeClick}
          dataId={payloadViewerDialogDataIdGenerator('close')}
        >
          <HdIcon name='close' size={3} />
        </HdIconButton>
      </div>

      <div className='dialog-body p-6  rr-block'>
        <div className='center-flex-row mb-5'>
          <div className='text-ellipsis text-subheading-3'>{title}</div>

          {subtitle && <span className='dialog-title-label'>({subtitle})</span>}
        </div>

        <div className={styles.progressBarWrapper}>
          {uiState.state === EntityUIState.LOADING && payload && <StickyProgressBar />}
        </div>

        {uiState.state === EntityUIState.LOADING && !payload && <PayloadViewerDialogShimmer />}

        {uiState.state !== EntityUIState.ERRORED &&
          uiState.state !== EntityUIState.EMPTY &&
          payload && (
            <>
              <div className='position-relative'>
                <JsonEditor
                  readOnly
                  allowCopy
                  code={payload}
                  showLineNumbers
                  className={styles.jsonEditor}
                  getCodeMirror={ed => {
                    editorRef.current = ed;
                  }}
                  dataId={payloadViewerDialogDataIdGenerator('')}
                />

                {showUserAccessError && <UserConsentError />}
              </div>

              {(renderNext || renderPrev) && (
                <div className='center-flex-row justify-center mt-7'>
                  {renderPrev && (
                    <HdButton
                      variation='outline'
                      icon='left-arrow'
                      className='mr-2'
                      disabled={uiState.state === EntityUIState.LOADING || !hasPrev}
                      dataId={payloadViewerDialogDataIdGenerator('previous')}
                      onClick={() => getPayload(PayloadViewerOrder.PREV)}
                    >
                      Previous
                    </HdButton>
                  )}

                  {renderNext && (
                    <HdButton
                      variation='outline'
                      icon='right-arrow'
                      disabled={uiState.state === EntityUIState.LOADING || !hasNext}
                      dataId={payloadViewerDialogDataIdGenerator('next')}
                      onClick={() => getPayload(PayloadViewerOrder.NEXT)}
                    >
                      Next
                    </HdButton>
                  )}
                </div>
              )}
            </>
          )}

        {uiState.state === EntityUIState.EMPTY && (
          <div className='no-item-box'>
            <div className='no-item-box-icon-container'>
              <HdIcon className='no-item-box-icon' name='sample-event' />
            </div>

            <span className='no-item-box-title'>Not Found</span>

            <span className='no-item-box-desc'>{uiState.emptyMsg}</span>
          </div>
        )}

        {uiState.state === EntityUIState.ERRORED && (
          <RetryApiAlert
            error={uiState.error}
            actionHandler={() => {
              getPayload(undefined);
            }}
            dataId={payloadViewerDialogDataIdGenerator('')}
          />
        )}
      </div>
    </>
  );
}
